import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Loader2, Image, Music, Video, File } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FileUpload = ({ 
  type = 'image', // 'image', 'music', 'video'
  value,
  onChange,
  onUploadComplete, // Optional callback for additional upload data (like auto-generated thumbnail)
  label,
  accept,
  className = ''
}) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Get stored credentials
  const getAuthHeader = () => {
    const stored = sessionStorage.getItem('adminCredentials');
    if (stored) {
      const { username, password } = JSON.parse(stored);
      return 'Basic ' + btoa(`${username}:${password}`);
    }
    return '';
  };

  const getAcceptTypes = () => {
    if (accept) return accept;
    switch (type) {
      case 'image':
        return 'image/jpeg,image/png,image/webp,image/gif';
      case 'music':
        return 'audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/m4a,.mp3,.wav,.ogg,.m4a,.aac';
      case 'video':
        return 'video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov,.avi';
      default:
        return '*';
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'image':
        return Image;
      case 'music':
        return Music;
      case 'video':
        return Video;
      default:
        return File;
    }
  };

  const Icon = getIcon();

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const authHeader = getAuthHeader();
      if (!authHeader) {
        toast.error('Not authenticated. Please log in again.');
        setUploading(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/admin/upload/${type}`, {
        method: 'POST',
        headers: {
          'Authorization': authHeader
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        // Store relative URL for domain independence
        onChange(data.url);
        // Call optional callback with full response data (includes thumbnail for videos)
        if (onUploadComplete) {
          onUploadComplete(data);
        }
        toast.success(`${file.name} uploaded successfully`);
      } else {
        let errorMsg = 'Upload failed';
        try {
          const error = await response.json();
          errorMsg = error.detail || `Upload failed (${response.status})`;
        } catch {
          errorMsg = `Upload failed with status ${response.status}`;
        }
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(`Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleClear = () => {
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUrlChange = (e) => {
    onChange(e.target.value);
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {label && (
        <label className="text-sm font-medium flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          {label}
        </label>
      )}

      {/* Preview area */}
      {value && (
        <div className="relative inline-block">
          {type === 'image' && (
            <div className="relative w-32 h-32 rounded-lg overflow-hidden bg-muted">
              <img 
                src={value} 
                alt="Preview"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <Button
                variant="destructive"
                size="icon"
                className="absolute top-1 right-1 h-6 w-6"
                onClick={handleClear}
                type="button"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}
          
          {type === 'music' && (
            <div className="flex items-center gap-3 p-3 bg-secondary rounded-lg">
              <Music className="h-8 w-8 text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate max-w-xs">{value.split('/').pop()}</p>
                <audio controls className="w-full mt-2 h-8">
                  <source src={value} />
                </audio>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                onClick={handleClear}
                type="button"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
          
          {type === 'video' && (
            <div className="relative">
              <video 
                src={value} 
                className="w-48 h-32 rounded-lg object-cover bg-muted"
                controls
              />
              <Button
                variant="destructive"
                size="icon"
                className="absolute top-1 right-1 h-6 w-6"
                onClick={handleClear}
                type="button"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Upload button */}
      <div className="flex items-center gap-3">
        <input
          ref={fileInputRef}
          type="file"
          accept={getAcceptTypes()}
          onChange={handleFileSelect}
          className="hidden"
          id={`file-upload-${type}-${label}`}
        />
        
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="flex-shrink-0"
        >
          {uploading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="h-4 w-4 mr-2" />
              Upload {type === 'image' ? 'Image' : type === 'music' ? 'Audio' : 'Video'}
            </>
          )}
        </Button>
        
        <span className="text-xs text-muted-foreground">or paste URL below</span>
      </div>

      {/* URL input */}
      <input
        type="text"
        value={value || ''}
        onChange={handleUrlChange}
        placeholder={`Or enter ${type} URL...`}
        className="w-full px-3 py-2 text-sm bg-secondary border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
      />
    </div>
  );
};

export default FileUpload;
